import type { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { groupContiguousKomaRanges } from '~/models/contiguousKomaRanges'
import { komaNumberToTime } from '~/models/komaToTime'

/**
 * 低圧未着のコマか (= 速報が未着の電圧階級が 1 つでもある)。 空 = 確定。
 * 判定は列挙の中身を見ずに長さだけで行う — 未知の enum 値で印を落とすと、
 * 低圧の欠けた値が確定値として読まれる。
 *
 * 状態名は階級によらず「低圧未着」 (spec / design 唯一の状態名)。 0110 欠落 ({特別高圧, 高圧}) の表示は PdM / Des 確認待ちのため、 階級から名前を組み立てない。
 */
export function isAwaitingVoltage(
  awaitingVoltageClasses: readonly VoltageClass[],
): boolean {
  return awaitingVoltageClasses.length > 0
}

/** 低圧未着のコマが 1 つでもあるか (凡例の見本を出す条件)。 */
export function hasAwaitingKoma(
  rows: ReadonlyArray<{ awaitingVoltageClasses: readonly VoltageClass[] }>,
): boolean {
  return rows.some((r) => isAwaitingVoltage(r.awaitingVoltageClasses))
}

/** チャートの網掛け 1 区間 (X 軸の時刻ラベル)。 */
export type AwaitingKomaRange = { x1: string; x2: string }

/**
 * 低圧未着のコマの連続区間を網掛けの区間にする。 区間の右端は最後のコマの終わり
 * (= 次のコマの頭)。 X 軸は 23:30 で終わるので、 48 コマ目を含む区間は 23:30 で
 * 打ち切る (GC 以降網掛けと同じ制約。 48 コマ目単独だと幅 0 になる)。
 */
export function awaitingKomaRanges(
  rows: ReadonlyArray<{
    koma: number
    awaitingVoltageClasses: readonly VoltageClass[]
  }>,
): AwaitingKomaRange[] {
  const komas = rows
    .filter((r) => isAwaitingVoltage(r.awaitingVoltageClasses))
    .map((r) => r.koma)
    .sort((a, b) => a - b)
  return groupContiguousKomaRanges(komas).map(({ start, end }) => ({
    x1: komaNumberToTime(start),
    x2: komaNumberToTime(Math.min(end + 1, KOMA_COUNT)),
  }))
}
